// src/components/OPDSupportReceived.js 
import React, { useEffect, useState, useMemo } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { 
  Package, 
  Calendar, 
  PieChart, 
  Filter, 
  Search, 
  Download, 
  ChevronDown, 
  ChevronUp, 
  ChevronsUpDown,
  BarChart3,
  TrendingUp,
  Award,
  Plus
} from "lucide-react";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

export default function OPDSupportReceived() {
  const [supports, setSupports] = useState([]);
  const [opdName, setOpdName] = useState("");
  const [loading, setLoading] = useState(true);

  // Enhanced filters
  const [filters, setFilters] = useState({
    year: "",
    type: "",
    search: "",
    startDate: "",
    endDate: "",
    organization: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "dateGiven", direction: "desc" });
  const itemsPerPage = 10;

  const SUPPORT_CATEGORIES = [
    "Materials",
    "Training",
    "Job",
    "Education",
    "Healthcare",
    "Financial Support",
    "Holiday & Welfare Support",
    "Other",
  ];

  const [newSupportType, setNewSupportType] = useState("");
  const [newSupportDescription, setNewSupportDescription] = useState("");
  const [newSupportAmount, setNewSupportAmount] = useState("");
  const [newSupportDate, setNewSupportDate] = useState("");
  const [newSupportOrganization, setNewSupportOrganization] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchSupports();
  }, []);

  const fetchSupports = async () => {
    setLoading(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));
      const currentUserEmail = currentUser?.email;

      if (!currentUserEmail) {
        setLoading(false);
        return;
      }

      // Resolve OPD from backend using contact_email
      const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
      const profData = await profRes.json();

      if (!(profData?.status === 'success' && profData?.data?.name)) {
        setLoading(false);
        return;
      }

      const name = profData.data.name;
      setOpdName(name);

      // Load supports for this OPD from backend per-OPD supports table
      const supportsRes = await fetch(
        `${API_BASE_URL}/get_opd_supports.php?opdName=${encodeURIComponent(name)}`
      );
      const supportsJson = await supportsRes.json();
      const supportsList =
        supportsJson?.status === 'success' && Array.isArray(supportsJson.data)
          ? supportsJson.data
          : [];
      setSupports(supportsList);
    } catch (error) {
      console.error("Failed to fetch OPD supports:", error);
    }
    setLoading(false);
  };

  const handleAddOtherSupport = async () => {
    const type = (newSupportType || "").trim();
    const description = (newSupportDescription || "").trim();
    const organization = (newSupportOrganization || "").trim();
    const amount = parseInt(newSupportAmount, 10);
    const dateGiven = newSupportDate;

    if (!type || !description || !organization || !dateGiven || !amount) {
      alert("Please select support type, write description, enter organization, enter amount, and pick date.");
      return;
    }

    if (!opdName) {
      alert("OPD not loaded yet. Please wait and try again.");
      return;
    }

    try {
      await fetch(`${API_BASE_URL}/add_opd_support.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          opdName,
          supportType: type,
          organization,
          amount,
          dateGiven,
          notes: description,
        }),
      });

      setNewSupportType("");
      setNewSupportDescription("");
      setNewSupportOrganization("");
      setNewSupportAmount("");
      setNewSupportDate("");
      setShowAddModal(false);

      await fetchSupports();
    } catch (error) {
      console.error("Failed to add other support:", error);
      alert("Failed to add support. Please try again.");
    }
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  // Enhanced filtering
  const filteredSupports = useMemo(() => {
    return supports.filter((s) => {
      const year = new Date(s.dateGiven).getFullYear();
      const matchesYear = filters.year ? year.toString() === filters.year : true;
      const matchesType = filters.type ? s.supportType === filters.type : true;
      const orgLabel = s.organization || "Association";
      const matchesOrg = filters.organization ? orgLabel === filters.organization : true;
      
      const matchesSearch = filters.search
        ? s.supportType.toLowerCase().includes(filters.search.toLowerCase()) ||
          s.dateGiven.includes(filters.search)
        : true;
      
      const matchesStartDate = filters.startDate 
        ? new Date(s.dateGiven) >= new Date(filters.startDate) 
        : true;
      const matchesEndDate = filters.endDate 
        ? new Date(s.dateGiven) <= new Date(filters.endDate) 
        : true;

      return matchesYear && matchesType && matchesOrg && matchesSearch && matchesStartDate && matchesEndDate;
    });
  }, [supports, filters]);

  // Sorting
  const sortedSupports = useMemo(() => {
    const sorted = [...filteredSupports];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key] ?? "";
        let bVal = b[sortConfig.key] ?? "";
        
        if (sortConfig.key === "dateGiven") {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        } else if (sortConfig.key === "amount") {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }
        
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredSupports, sortConfig]);

  const paginatedSupports = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedSupports.slice(start, start + itemsPerPage);
  }, [sortedSupports, currentPage]);

  const totalPages = Math.ceil(sortedSupports.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  // Years, types, and organizations available for filter
  const availableYears = [...new Set(supports.map((s) => new Date(s.dateGiven).getFullYear()))];
  const availableTypes = [...new Set(supports.map((s) => s.supportType))];
  const availableOrganizations = [
    ...new Set(
      supports
        .map((s) => s.organization || "Association")
        .filter((org) => org && org.trim() !== "")
    ),
  ];

  const exportCSV = () => {
    const headers = ["Support Type", "Amount", "Date Given"];
    const rows = sortedSupports.map((s) => [
      s.supportType,
      s.amount || "",
      s.dateGiven,
    ]);

    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers, ...rows].map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `opd-supports-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 📊 Summary Calculations
  const totalSupports = filteredSupports.length;
  const totalAmount = filteredSupports.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

  const typeCount = filteredSupports.reduce((acc, s) => {
    acc[s.supportType] = (acc[s.supportType] || 0) + 1;
    return acc;
  }, {});
  
  const mostCommonType = Object.keys(typeCount).length > 0
    ? Object.entries(typeCount).sort((a, b) => b[1] - a[1])[0][0]
    : "-";

  const thisMonth = new Date().getMonth();
  const supportsThisMonth = filteredSupports.filter(
    (s) => new Date(s.dateGiven).getMonth() === thisMonth
  ).length;

  // 📊 Chart Data
  const supportsByType = {
    labels: Object.keys(typeCount),
    datasets: [
      {
        data: Object.values(typeCount),
        backgroundColor: [
          "#0d9488", "#1976d2", "#388e3c", "#fbc02d", "#d32f2f", 
          "#7b1fa2", "#f57c00", "#00796b", "#616161", "#e91e63"
        ],
        hoverOffset: 6,
        borderWidth: 2,
        borderColor: "#ffffff",
      },
    ],
  };

  const monthlyCount = Array(12).fill(0);
  filteredSupports.forEach((s) => {
    const month = new Date(s.dateGiven).getMonth();
    monthlyCount[month] += 1;
  });

  const supportsByMonth = {
    labels: [
      "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec",
    ],
    datasets: [
      {
        label: "Supports Received",
        data: monthlyCount,
        backgroundColor: "#0d9488",
        borderColor: "#0f766e",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <div className="grid grid-cols-3 items-center gap-4 mb-2">
          <div className="flex items-center gap-3 col-span-2">
            <Award className="w-7 h-7 text-teal-700" />
            <div>
              <h1 className="text-3xl font-semibold text-gray-900">
                {opdName ? `Supports Received - ${opdName}` : "Supports Received"}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Summary of supports recorded for this organization.
              </p>
            </div>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
            >
              <Plus className="w-4 h-4" />
              Add Support
            </button>
          </div>
        </div>
      </header>

      {/* Control Panel */}
      <div className="bg-white px-4 py-3 rounded-xl shadow-sm mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end">
          {/* Search Input */}
          <div className="relative md:col-span-2 lg:col-span-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search by support type or date..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
            />
          </div>

          {/* Year Filter */}
          <select
            name="year"
            value={filters.year}
            onChange={onFilterChange}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Years</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            name="type"
            value={filters.type}
            onChange={onFilterChange}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Types</option>
            {availableTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          {/* Support Organization Filter */}
          <select
            name="organization"
            value={filters.organization}
            onChange={onFilterChange}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Organizations</option>
            {availableOrganizations.map((org) => (
              <option key={org} value={org}>
                {org}
              </option>
            ))}
          </select>

          {/* Date Filters */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={onFilterChange}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
              placeholder="Start Date"
            />
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={onFilterChange}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500 transition"
              placeholder="End Date"
            />
          </div>

          {/* Export Button */}
          <div className="flex items-center justify-end">
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap"
              onClick={exportCSV}
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-16">Loading support data...</p>
      ) : filteredSupports.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No support data found matching your criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Statistics Cards */}
          <div className="xl:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Supports</p>
                    <p className="text-2xl font-semibold text-gray-900 mt-1">{totalSupports}</p>
                  </div>
                  <Package className="w-8 h-8 text-teal-600" />
                </div>
              </div>

              <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Amount</p>
                    <p className="text-2xl font-semibold text-gray-900 mt-1">{totalAmount}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-emerald-600" />
                </div>
              </div>

              <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Most Common Type</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1 truncate">{mostCommonType}</p>
                  </div>
                  <PieChart className="w-8 h-8 text-indigo-600" />
                </div>
              </div>

              <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">This Month</p>
                    <p className="text-2xl font-semibold text-gray-900 mt-1">{supportsThisMonth}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-sky-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="xl:col-span-2">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
                <h3 className="text-lg font-semibold text-gray-800">Support Details</h3>
              </div>

              {/* Table for Medium screens and up */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        { key: 'supportType', label: 'Support Type' },
                        { key: 'organization', label: 'Organization' },
                        { key: 'amount', label: 'Amount' },
                        { key: 'dateGiven', label: 'Date Given' },
                      ].map(col => (
                        <th 
                          key={col.key} 
                          onClick={() => requestSort(col.key)}
                          className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-2">
                            {col.label}
                            {sortConfig.key === col.key ? (
                              sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                            ) : <ChevronsUpDown className="w-4 h-4 text-gray-400" />}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedSupports.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-gray-700 font-medium">
                          {s.notes ? `${s.supportType}: ${s.notes}` : s.supportType}
                        </td>
                        <td className="py-3 px-4 text-gray-600">{s.organization || "Association"}</td>
                        <td className="py-3 px-4 text-gray-600">{s.amount || "-"}</td>
                        <td className="py-3 px-4 text-gray-600">{new Date(s.dateGiven).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cards for Small screens */}
              <div className="block md:hidden">
                <div className="divide-y divide-gray-200">
                  {paginatedSupports.map((s) => (
                    <div key={s.id} className="p-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <p className="font-bold text-gray-800">
                            {s.notes ? `${s.supportType}: ${s.notes}` : s.supportType}
                          </p>
                          <span className="text-sm text-gray-500">{new Date(s.dateGiven).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-gray-600">Organization: {s.organization || "Association"}</p>
                        <p className="text-sm text-gray-600">Amount: {s.amount || "-"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 gap-4">
                <span className="text-sm text-gray-600">
                  Showing {paginatedSupports.length} of {sortedSupports.length} supports
                </span>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="xl:col-span-1 space-y-6">
            {/* Pie Chart */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center gap-2 mb-4">
                <PieChart className="w-5 h-5 text-teal-600" />
                <h3 className="text-lg font-semibold text-gray-800">Supports by Type</h3>
              </div>
              <div className="h-64">
                <Pie 
                  data={supportsByType} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 20,
                          usePointStyle: true,
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>

            {/* Bar Chart */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-teal-600" />
                <h3 className="text-lg font-semibold text-gray-800">Monthly Distribution</h3>
              </div>
              <div className="h-64">
                <Bar
                  data={supportsByMonth}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { 
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        borderColor: '#0d9488',
                        borderWidth: 1,
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)',
                        }
                      },
                      x: {
                        grid: {
                          display: false,
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg mx-4">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">Add support from other organizations</h3>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Close
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Support Type</label>
                <select
                  value={newSupportType}
                  onChange={(e) => setNewSupportType(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                >
                  <option value="">Select Support Type</option>
                  {SUPPORT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <input
                  type="text"
                  value={newSupportDescription}
                  onChange={(e) => setNewSupportDescription(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  placeholder="e.g. 10 wheelchairs from NGO X"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Organization Providing Support</label>
                <input
                  type="text"
                  value={newSupportOrganization}
                  onChange={(e) => setNewSupportOrganization(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  placeholder="e.g. NGO X, Ministry of Health"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                  <input
                    type="number"
                    min="1"
                    value={newSupportAmount}
                    onChange={(e) => setNewSupportAmount(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={newSupportDate}
                    onChange={(e) => setNewSupportDate(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddOtherSupport}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
              >
                <Plus className="w-4 h-4" />
                Add Support
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
